import { observer } from 'mobx-react-lite';
import React, { useMemo } from 'react';

import { Panel, PanelContent, PanelHeader, Spinner } from '@flixbus/honeycomb-react';

import { assignJobReturnSidebarStore } from 'pages/WorkAssignments/components/AssignJobReturnSidebar/AssignJobReturnSidebarStore';

import { mapAssignmentToCard } from './mapAssignmentToCard';
import ReturnJobCard from './ReturnJobCard';
import { returnJobDetailsSidebarText } from './text';

import styles from './styles.module.css';

const ReturnJobDetailsSidebar = observer(
	({
		active = true,
		selectedAssignmentKey = null,
		primarySequenceGroupId = null,
		assignment = null,
		assignments = [],
		loading = false,
		onClose = () => {},
	}) => {
		const cards = useMemo(() => {
			const source = Array.isArray(assignments) ? assignments : assignment ? [assignment] : [];

			return source
				.filter((item) => {
					if (!primarySequenceGroupId) return true;
					return String(item?.sequenceGroupId) !== String(primarySequenceGroupId);
				})
				.map(mapAssignmentToCard);
		}, [assignments, assignment, primarySequenceGroupId]);

		const shouldShowSelectedReturnJob =
			assignJobReturnSidebarStore.showSelectedReturnJob &&
			assignJobReturnSidebarStore.selectedAssignmentId === selectedAssignmentKey;

		const selectedCard = useMemo(() => {
			if (!assignJobReturnSidebarStore.selectedReturnJob) return null;
			return (
				cards.find((card) => card.key === assignJobReturnSidebarStore.selectedReturnJob.key) ?? null
			);
		}, [cards]);

		const visibleCards = useMemo(() => {
			if (!shouldShowSelectedReturnJob || !selectedCard) return cards;
			return cards.filter((card) => card.key !== selectedCard.key);
		}, [cards, selectedCard, shouldShowSelectedReturnJob]);

		return (
			<Panel
				id="return-job-details-sidebar"
				active={active}
				extraClasses={`${styles.panelWidth} ${styles.nearAssignSidebar}`}
				position="default"
			>
				<PanelHeader closeButtonProps={{ label: 'Close', onClick: onClose }}>
					{returnJobDetailsSidebarText.title}
				</PanelHeader>
				<PanelContent extraClasses={styles.panelWidth}>
					<div className={styles.scroll}>
						<div className={styles.content}>
							{loading ? (
								<div className={styles.loadingState}>
									<Spinner />
								</div>
							) : cards.length > 0 ? (
								<div className={styles.sequenceList}>
									{shouldShowSelectedReturnJob && selectedCard && (
										<>
											<div className={styles.sectionLabel}>
												{returnJobDetailsSidebarText.sectionLabelSelected}
											</div>
											<ReturnJobCard card={selectedCard} variant="selected" />
											{visibleCards.length > 0 && (
												<div className={styles.sectionLabel}>
													{returnJobDetailsSidebarText.sectionLabelOther}
												</div>
											)}
										</>
									)}
									{visibleCards.map((card) => (
										<ReturnJobCard
											key={card.key}
											card={card}
											onSelect={() => {
												assignJobReturnSidebarStore.selectReturnJob(selectedAssignmentKey, card);
												onClose();
											}}
										/>
									))}
								</div>
							) : (
								<div className={styles.emptyState}>
									{returnJobDetailsSidebarText.noReturnJobsFound}
								</div>
							)}
						</div>
					</div>
				</PanelContent>
			</Panel>
		);
	}
);

export default ReturnJobDetailsSidebar;
